// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { computed, ref, watch, onMounted } from 'vue'
import { useStore } from '@nanostores/vue'
import { useElementVisibility, useWindowSize } from '@vueuse/core'
import axios from 'axios'
import { isLogin } from '@/store/userStore'
import {
	type HeroSectionData,
	type HeroButtonType,
} from '@/components/HeroSection.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import { type BannerSectionData } from '@/components/PromoBannerSection.vue'
import BlogThumbnail from '@/components/BlogThumbnail.vue'
import hybridPage from '@/lib/hybridPage'
import ProductSharingFeatureContainer from '@/legacy/components/ProductSharingFeatureContainer.vue'
import MKTBannerSection from '@/components/MKTBannerSection.vue'
import ShoppingDestinationSection from '@/components/ShoppingDestinationSection.vue'
import CombinedFlowSection from '@/components/CombinedFlowSection.vue'
import ValuePropositionSection from '@/components/ValuePropositionSection.vue'
import QuoteBox from '@/components/QuoteBox.vue'
import CouponCenterEntry from '@/components/CouponCenterEntry.vue'
import {
	getProductSharingUrl,
	checkProductSharingEnable,
} from '@/legacy/ts/productSharing'
import { type RegistrationCardData } from '@/components/RegistrationCard.vue'
import { useTestimonial } from '@/lib/useTestimonial'
import {
	getCreditText,
	useBreakpoints,
	setCurrentGaPageName,
} from '@/lib/utils'
import { checkIsPhActivation, getPhLayoutType } from '@/lib/checkService'
import DialogCenter from '@/components/DialogCenter.vue'
import HeroSectionPh from '@/components/HeroSectionPh.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import { useFetchShoppingData } from '@/lib/useFetchShoppingData'
import { type IBlogThumbnailData } from '@/types/Blog'

interface CouponCentreEntry {
	image_url: string
	button_text: string
	link_url: string
}

hybridPage()

createApp({
	name: 'Index',
	components: {
		BaseButton,
		BlogThumbnail,
		ProductSharingFeatureContainer,
		QuoteBox,
		CouponCenterEntry,
		MKTBannerSection,
		DialogCenter,
		ShoppingDestinationSection,
		CombinedFlowSection,
		ValuePropositionSection,
		HeroSectionPh,
	},
	setup() {
		setCurrentGaPageName(GaGeneralPageName.HomePage)
		const { isLarge } = useBreakpoints()
		const { swiperEl, marginStyle, goPrev, goNext } = useTestimonial()

		// ---------------------
		// Coupon Centre Entry
		// ---------------------
		const couponCentreEntry = ref<CouponCentreEntry | null>(null)
		// ---------------------
		// Registration Card Section Start
		// ---------------------
		const registrationCardData: RegistrationCardData =
			window.pageContent.registrationContent
		const currentRegistrationCardContent = computed(() => {
			return isLoginGlobal.value
				? registrationCardData.afterLogin
				: registrationCardData.beforeLogin
		})
		const currentRegistrationCardTitle1 = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.title1)
		})
		const currentRegistrationCardTitle2 = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.title2)
		})
		const WELCOME_CREDIT = window.BUYANDSHIP?.settings?.member_welcome_credits
		const currentRegistrationCardDesc = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.description)
		})
		const bgRegistrationCardClass = computed(() => {
			return currentRegistrationCardContent.value.class
		})
		const openReferral = (link?: string) => {
			if (link) {
				window.location.href = link
				return
			}
			window.location.href = getProductSharingUrl('referral')
		}
		const openRegister = () => {
			window.BsHeaderVm.showSignupPanel()

			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.HomePage,
				button_name: 'Sign Up - Bottom',
			})
		}
		const handleRegistrationCardClick = (link?: string) => {
			if (isLoginGlobal.value) {
				openReferral(link)
			} else {
				openRegister()
			}
		}
		// ---------------------
		// Registration Card Section End
		// ---------------------
		const testimonialData = window.pageContent.testimonialContent
		const { postCardTextInfo } = window.pageContent
		const { postListText } = window.pageContent
		const { sideText } = window.pageContent
		const isLoginGlobal = useStore(isLogin)
		const heroSectionData: HeroSectionData = window.pageContent.heroContent
		const promoBtnEl = ref<HTMLElement | null>(null)
		const isPromoVisible = useElementVisibility(promoBtnEl)
		const isShowDownloadAppDialog = ref(false)
		const isLayoutOriginal = computed(() => getPhLayoutType() === 'original')
		const isLayoutA = getPhLayoutType() === 'A'
		const currentHeroSectionData = computed(() => {
			return isLoginGlobal.value
				? heroSectionData.afterLogin
				: heroSectionData.beforeLogin
		})
		const currentPromoBtn = computed(() => {
			return getCreditText(currentHeroSectionData.value.promoBtn || '')
		})

		const isCurrentRegionJp = computed(() => {
			return window.BUYANDSHIP.settings?.current_country === 'jp'
		})

		function handleHeroSectionButtonClick(buttonType: HeroButtonType) {
			if (buttonType === 'primary') {
				if (currentHeroSectionData.value.primaryIsRegister) {
					window.BsHeaderVm.showSignupPanel()
					createGAEvent(GaGeneralEvent.ButtonClick, {
						page_name: GaGeneralPageName.HomePage,
						button_name: 'Sign Up - Hero',
					})
				} else {
					const fullUrl = `${getSiteBaseUrl()}${
						currentHeroSectionData.value.primaryBtnLink
					}`
					createGAEvent(GaGeneralEvent.ButtonClick, {
						page_name: GaGeneralPageName.HomePage,
						button_name: 'Hero Button A',
						event_extra: fullUrl,
					})

					window.location.href =
						currentHeroSectionData.value.primaryBtnLink || ''
				}
			} else if (buttonType === 'secondary') {
				const fullUrl = `${getSiteBaseUrl()}${
					currentHeroSectionData.value.secondaryBtnLink
				}`
				createGAEvent(GaGeneralEvent.ButtonClick, {
					page_name: GaGeneralPageName.HomePage,
					button_name: 'Hero Button B',
					event_extra: fullUrl,
				})

				window.location.href =
					currentHeroSectionData.value.secondaryBtnLink || ''
			}
		}

		function onClickPromoBannerButton() {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.HomePage,
				button_name: 'MKT Banner Click',
			})
		}

		function onClickViewMoreBlog() {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: GaGeneralPageName.HomePage,
				button_name: 'View All Blogs',
			})
		}

		watch(isPromoVisible, isVisible => {
			if (isVisible) {
				promoBtnEl.value?.classList.add('animate-horizontal-shake')
			} else {
				promoBtnEl.value?.classList.remove('animate-horizontal-shake')
			}
		})

		watch(
			() => isLarge.value,
			value => {
				if (!value && isShowPHActivation.value) {
					isShowDownloadAppDialog.value = false
				}
			},
		)

		// Promo Banner
		const bannerSectionData = ref<null | BannerSectionData>(null)
		const apiUrl = computed(() => {
			return `${window.BUYANDSHIP.settings.content_api_url}app-index/`
		})
		const isLoadingPromo = computed(() => {
			return bannerSectionData.value == null
		})
		const currentPromoData = computed(() => {
			if (bannerSectionData == null) return {}

			return {
				title: isLoginGlobal.value
					? bannerSectionData.value?.title.login
					: bannerSectionData.value?.title.logout,
				subtitle: isLoginGlobal.value
					? bannerSectionData.value?.subtitle.login
					: bannerSectionData.value?.subtitle.logout,
				button_title: isLoginGlobal.value
					? bannerSectionData.value?.button_title.login
					: bannerSectionData.value?.button_title.logout,
				website_button_destination: isLoginGlobal.value
					? bannerSectionData.value?.website_button_destination.login
					: bannerSectionData.value?.website_button_destination.logout,
				app_button_destination: isLoginGlobal.value
					? bannerSectionData.value?.app_button_destination.login
					: bannerSectionData.value?.app_button_destination.logout,
				app_hidden_after_login: bannerSectionData.value?.app_hidden_after_login,
				website_is_hidden_banner_mobile:
					bannerSectionData.value?.website_is_hidden_banner_mobile,
				images: bannerSectionData.value?.images,
			}
		})

		const { width } = useWindowSize()
		const currentPromoImg = computed(() => {
			const viewportWidth = width.value
			const imgList = bannerSectionData.value?.images?.[0].website

			if (!imgList || imgList.length === 0) return '' // Return an empty string if there are no images

			const selectedImage =
				[...imgList]
					.reverse()
					.find(image => viewportWidth >= image.breakpoint) || imgList[0]

			return `background-image: url(${selectedImage.url})`
		})

		// Latest News
		const { newsTabs } = window.pageContent
		const blogPostsByTab = ref<{ [key: string]: IBlogThumbnailData[] }>({})
		const currentTab = ref(0)
		const loadedTabs = ref(new Set<number>())
		const isNewsDataLoaded = computed(() => {
			return loadedTabs.value.has(currentTab.value)
		})

		// Returns the blog list based on the current tab
		const currentTabBlogPosts = computed(() => {
			const tabKey = currentTab.value.toString()
			return blogPostsByTab.value[tabKey] || []
		})

		function activeClasses(index: number) {
			return currentTab.value === index ? 'bg-black text-white' : ''
		}

		function clickTab(index: number) {
			currentTab.value = index
			// Lazy load blog data when tab is clicked
			fetchAndStoreBlogPosts(index)
		}

		// Builds the API URL for fetching blog posts.
		function buildBlogPostAPIUrl(tabIndex: number): string {
			const { include, exclude } = newsTabs[tabIndex]
			const queryParams = [
				'page=1',
				'limit=6',
				...include.map((cat: string) => `cats[]=${encodeURIComponent(cat)}`),
				...exclude.map(
					(cat: string) => `ignore_cats[]=${encodeURIComponent(cat)}`,
				),
			]
			return `${window.BUYANDSHIP.settings.api_url}blog?${queryParams.join(
				'&',
			)}`
		}

		// Fetches and store blog data for a given tab index
		const fetchAndStoreBlogPosts = async (tabIndex: number) => {
			if (loadedTabs.value.has(tabIndex)) return
			const apiUrl = buildBlogPostAPIUrl(tabIndex)

			try {
				const { data } = await axios.get(apiUrl)
				blogPostsByTab.value[tabIndex] = data.data.posts
			} catch (error) {
				console.error('Failed to fetch blog data:', error)
				blogPostsByTab.value[tabIndex] = []
			} finally {
				loadedTabs.value.add(tabIndex)
			}
		}

		const fetchBannerAndCouponData = async () => {
			try {
				const { data } = await axios.get(apiUrl.value)
				bannerSectionData.value = data.data.content

				// some region not have coupon centre entry
				couponCentreEntry.value = data.data.website_coupon_centre_entry ?? null
			} catch (error) {
				console.error('Failed to fetch banner and coupon data:', error)
			}
		}

		// PH Activation
		const isShowPHActivation = computed(() => {
			return (
				checkIsPhActivation() && !isLayoutOriginal.value && !isLoginGlobal.value
			)
		})

		const mktBannerData = ref<IBlogThumbnailData[]>([])

		const fetchMKTBannerData = async () => {
			try {
				const inAppIndexResponse = await axios.get(apiUrl.value)
				const mercariEntry =
					inAppIndexResponse.data.data.website_mercari_centre_entry

				// Set POST_LIMIT based on mercari entry existence
				const POST_LIMIT = mercariEntry ? 2 : 3
				const blogApiUrl = `${window.BUYANDSHIP.settings.api_url}blog?sticky=yes&limit=${POST_LIMIT}&page=1`

				// Get blog posts
				const blogResponse = await axios.get(blogApiUrl)
				const stickyPosts = blogResponse.data.data.posts.slice(0, POST_LIMIT)

				if (mercariEntry) {
					// Create mercari banner data in BlogThumbnailData format
					const mercariData: IBlogThumbnailData = {
						title: 'Mercari',
						date: new Date().toISOString(),
						link: mercariEntry.link_url,
						img_thumbnail: mercariEntry.image_url,
						img_medium: mercariEntry.image_url,
						img_large: mercariEntry.image_url,
						excerpt: '',
						tags: [],
						categories: [],
					}

					// Combine sticky posts with mercari data
					mktBannerData.value = [...stickyPosts, mercariData]
				} else {
					// Just use sticky posts if no mercari entry
					mktBannerData.value = stickyPosts
				}
			} catch (error) {
				console.error('Failed to fetch mkt banner data:', error)
			}
		}

		const { shoppingDestinationContent } = window.pageContent
		const { combinedFlowContent } = window.pageContent
		const { valuePropositionContent } = window.pageContent
		const { shoppingDestinationData, fetchShoppingDestinationData } =
			useFetchShoppingData()

		const initializeData = async () => {
			await Promise.all([
				fetchBannerAndCouponData(),
				fetchAndStoreBlogPosts(0), // Only load first tab initially
				checkIsPhActivation() && fetchMKTBannerData(),
				checkIsPhActivation() && fetchShoppingDestinationData(),
			])
		}

		onMounted(() => {
			initializeData()

			if (isShowPHActivation.value) {
				document.addEventListener('handle_download_app_dialog', (obj: any) => {
					isShowDownloadAppDialog.value = obj.detail.value
				})
			}

			createGAEvent('page_load', {
				page_name: GaGeneralPageName.HomePage,
			})
		})

		const isProductSharingEnable = ref(checkProductSharingEnable())

		return {
			WELCOME_CREDIT,
			currentHeroSectionData,
			promoBtnEl,
			currentPromoBtn,
			isCurrentRegionJp,
			handleHeroSectionButtonClick,

			// Promo Banner

			isLoadingPromo,
			currentPromoData,
			currentPromoImg,
			apiUrl,
			onClickPromoBannerButton,
			onClickViewMoreBlog,

			// Latest news

			newsTabs,
			currentTab,
			isNewsDataLoaded,
			currentTabBlogPosts,
			activeClasses,
			clickTab,

			// Product Sharing
			postCardTextInfo,
			postListText,
			sideText,

			// ================== Registration Card ==================
			currentRegistrationCardContent,
			currentRegistrationCardTitle1,
			currentRegistrationCardTitle2,
			currentRegistrationCardDesc,
			bgRegistrationCardClass,
			handleRegistrationCardClick,
			// ================== Testimonial Section ==================
			testimonialData,
			swiperEl,
			marginStyle,
			goPrev,
			goNext,
			isProductSharingEnable,
			couponCentreEntry,

			// ================== PH Activation ==================
			isShowPHActivation,
			mktBannerData,
			isShowDownloadAppDialog,
			shoppingDestinationContent,
			shoppingDestinationData,
			isLoginGlobal,
			combinedFlowContent,
			valuePropositionContent,
			isLayoutA,
		}
	},
}).mount('#bsMain')
